import is from '@sindresorhus/is';
import i18n from 'i18next';
import noop from 'lodash.noop';
import type { FunctionComponent, PropsWithChildren } from 'react';
import { useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import Maybe from 'true-myth/maybe';

import type { AuthorizationProvider } from '~/shared/authorization/authorization-provider';
import type { Clock } from '~/shared/clock/clock';
import { ApplicationContext } from '~/shared/context/context';
import type { CustomizationProvider } from '~/shared/customization/customization';
import type { SkillDrawerStates } from '~/shared/data-contract/skill-drawer';
import type { User } from '~/shared/data-contract/user';
import type { AppEnvironment } from '~/shared/environment/environment';
import type { FeatureFlagService } from '~/shared/feature-flag-service/feature-flag-service';
import type { HttpFetcherWithBaseUrl } from '~/shared/http-fetcher';
import type { LanguageCode, LanguageKey } from '~/shared/internationalization';
import type { Translate } from '~/shared/internationalization/translation';
import type { Logger } from '~/shared/logger/logger';
import type { RealtimeEventListenerBuilder } from '~/shared/realtime-event-listener/realtime-event-listener';
import type { ValidatingHttpFetcherWithBaseUrl } from '~/shared/validating-http-fetcher';

import { useOpenExternalLink } from '../components/ExternalLinkConfirmation';
import { useLanguage } from '../internationalization/internationalization';
import { useSnackbars } from './error-handling/SnackBars';
import { useMyProfileQuery } from './query-hooks/profile';
import { useUserSettingsUi, useUserSettingsUiMutation } from './query-hooks/use-user-settings-ui';

export type CreateTrackingServiceOptions = {
    readonly languageKey: LanguageKey;
    readonly mixpanelProjectId: Maybe<string>;
    readonly tenantId: Maybe<string>;
    readonly user: Maybe<User>;
};

export type AppDependencies = {
    readonly authorizationProvider: AuthorizationProvider;
    readonly clock: Clock;
    readonly cobrainerApiFetcher: HttpFetcherWithBaseUrl;
    readonly customizationProvider: CustomizationProvider;
    readonly featureFlagService: FeatureFlagService;
    readonly logger: Logger;
    readonly realtimeApiFetcher: HttpFetcherWithBaseUrl;
    readonly realtimeEventListenerBuilder: RealtimeEventListenerBuilder;
    readonly translate: Translate;
    readonly validatingCobrainerApiFetcher: ValidatingHttpFetcherWithBaseUrl;
    readonly validatingRealtimeApiFetcher: ValidatingHttpFetcherWithBaseUrl;
};

type ApplicationContextProps = {
    readonly dependencies: AppDependencies;
    readonly environment: AppEnvironment;
};

export const AppWithContext: FunctionComponent<PropsWithChildren<ApplicationContextProps>> = (props) => {
    const { children, dependencies, environment } = props;
    const openExternalLink = useOpenExternalLink();

    const allowedLanguages = dependencies.customizationProvider.customization
        .map((c): LanguageCode[] => c.commonConfiguration.languages ?? ['en-US' as const, 'de-DE' as const])
        .unwrapOr(['en-US' as const, 'de-DE' as const]);

    const { language, languageKey, languages } = useLanguage(allowedLanguages);
    const navigate = useNavigate();
    const { showErrorSnackbar } = useSnackbars();
    const {
        authorizationProvider,
        clock,
        cobrainerApiFetcher,
        customizationProvider,
        featureFlagService,
        logger,
        realtimeApiFetcher,
        realtimeEventListenerBuilder,
        translate,
        validatingCobrainerApiFetcher,
        validatingRealtimeApiFetcher,
    } = dependencies;

    const { userSettingsUiMutationOptimistic } = useUserSettingsUiMutation(validatingCobrainerApiFetcher);
    const [skillDrawerStates, setSkillDrawerStates] = useState<SkillDrawerStates | null>(null);

    const { isBlocked: userIsBlocked, isLoggedIn: userIsLoggedIn } = authorizationProvider;
    const [locationSearchParams, setLocationSearchParams] = useSearchParams();
    const location = useLocation();
    const { data: myUserProfileData } = useMyProfileQuery(validatingCobrainerApiFetcher, {
        isLoggedIn: userIsLoggedIn && userIsBlocked === false,
    });
    const myUserProfile = useMemo(() => {
        return Maybe.of(myUserProfileData);
    }, [myUserProfileData]);
    const uiSettingsQuery = useUserSettingsUi(validatingCobrainerApiFetcher, {
        enabled: myUserProfile.get('exists').unwrapOr(false),
    });
    const userIsAnonymized = Boolean(uiSettingsQuery.data?.dataAnonymization);
    const setCurrentLocationToLastBrowserHistoryItem = (): void =>
        globalThis.history.pushState({}, '', new URL(globalThis.location.href));

    dependencies.cobrainerApiFetcher.requestHeaders = {
        'Accept-Language': language.code,
    };

    dependencies.realtimeEventListenerBuilder.requestHeaders = {
        'Accept-Language': language.code,
    };
    dependencies.validatingCobrainerApiFetcher.requestHeaders = {
        'Accept-Language': language.code,
    };

    return (
        <ApplicationContext.Provider
            value={{
                actions: {
                    deleteLocationSearchParams: (paramsToDelete) => {
                        for (const param of paramsToDelete) {
                            locationSearchParams.delete(param);
                        }

                        setLocationSearchParams(locationSearchParams);
                    },
                    login: (providerId: number, callbackUri?: string) => {
                        authorizationProvider.login(providerId, callbackUri);
                    },
                    logout: () => {
                        authorizationProvider.logout();
                    },
                    mailTo: (recipient) => {
                        if (is.nonEmptyString(recipient.email)) {
                            const mailtoLink = `
                                mailto:${recipient.email}?subject=${encodeURIComponent(
                                    recipient.subject,
                                )}&body=${encodeURIComponent(recipient.body)}
                                `;
                            window.open(mailtoLink);
                        }
                    },
                    navigateBack: () => navigate(-1),
                    navigateTo: navigate,
                    openSkillDetails: (skillId) => {
                        locationSearchParams.set('qDrawerName', 'skill-details-drawer');
                        locationSearchParams.set('qDrawerSkillDetailsId', skillId);

                        setLocationSearchParams(locationSearchParams, { replace: true });
                    },
                    openWindow: (url) => {
                        if (is.nonEmptyString(url)) {
                            openExternalLink(url);
                        }
                    },
                    refetchMyUserProfile: noop,
                    resetCustomization: customizationProvider.reset,
                    setCurrentLocationToLastBrowserHistoryItem,
                    setLanguage: i18n.changeLanguage,
                    setLocationSearchParams,
                    setOnboardingStatus: noop,
                    setSkillDrawerStates,
                    toggleAnonymization: () => {
                        userSettingsUiMutationOptimistic.mutate(
                            { dataAnonymization: userIsAnonymized === false },
                            { onError: showErrorSnackbar },
                        );
                    },
                },
                applicationState: {
                    availableLanguages: languages,
                    clock,
                    customization: customizationProvider.customization,
                    featureFlagService,
                    hideLogoutButtons: authorizationProvider.hideLogoutButtons,
                    isAssumedTenant: customizationProvider.assumedTenantId.isJust,
                    isDefaultCustomization: customizationProvider.isDefaultCustomization,
                    isPendingMyUserProfile: false,
                    isSmallScreen: false,
                    language,
                    languageKey,
                    location,
                    locationSearchParams,
                    myUserProfile,
                    onboardingStatus: 'finished',
                    skillDrawerStates,
                    uiSettings: Maybe.of(uiSettingsQuery.data),
                    userIsAnonymized,
                    userIsBlocked,
                    userIsLoggedIn,
                },
                cobrainerApiFetcher,
                environment,
                logger,
                realtimeApiFetcher,
                realtimeEventListenerBuilder,
                translate,
                validatingCobrainerApiFetcher,
                validatingRealtimeApiFetcher,
            }}
        >
            {children}
        </ApplicationContext.Provider>
    );
};
